import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { GlobeIcon, RefreshIcon, ServerIcon, SettingsIcon, TrashIcon } from '@/components/icons';
import { StatusIndicator } from '@/components/StatusIndicator';
import { getNodeType } from '@/config/nodeTypes';
import { nodeStatusMap } from '@/config/statusMaps';
import { formatRelativeTime } from '@/utils/date';
import type { Node } from '@/types/node';

/**
 * Row actions of the nodes table
 */
export type NodeRowAction = 'pleskDomains' | 'health' | 'edit' | 'delete';

export interface NodesTableProps {
  nodes: Node[];
  isLoading: boolean;
  /** Shown in the empty state when set (user may add nodes) */
  onAddFirst?: () => void;
  getActions: (node: Node) => Record<NodeRowAction, boolean>;
  isBusy: (action: NodeRowAction, nodeId: string) => boolean;
  onAction: (action: NodeRowAction, node: Node) => void;
}

const COLUMN_COUNT = 7;

/**
 * CDN nodes table. Type labels and per-type badges come from the node type registry.
 */
export function NodesTable({ nodes, isLoading, onAddFirst, getActions, isBusy, onAction }: NodesTableProps) {
  return (
    <Table isLoading={isLoading} skeletonColumns={COLUMN_COUNT} skeletonRows={5}>
      <TableHead>
        <TableRow>
          <TableHeader>Name</TableHeader>
          <TableHeader>IP Address</TableHeader>
          <TableHeader>Type</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Mode</TableHeader>
          <TableHeader>Last Heartbeat</TableHeader>
          <TableHeader align="right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {nodes.length === 0 && !isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <TableEmpty
                icon={<ServerIcon />}
                message="No CDN nodes registered yet"
                action={
                  onAddFirst && (
                    <Button variant="secondary" size="sm" onClick={onAddFirst}>
                      Add your first node
                    </Button>
                  )
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          nodes.map((node) => {
            const nodeType = getNodeType(node.type);
            const actions = getActions(node);

            return (
              <TableRow key={node.id}>
                <TableCell>
                  <div className="nodes-name-cell">
                    <span className="nodes-name">
                      {node.name}
                      {node.has_agent_token === false && (
                        <span
                          className="nodes-token-badge"
                          title="No agent API token configured - the agent API is unauthenticated or unreachable"
                        >
                          No token
                        </span>
                      )}
                    </span>
                    <span className="nodes-hostname">{node.hostname}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="nodes-ip-cell">
                    <code className="nodes-ip">{node.ip_address}</code>
                    {node.ipv6_address && (
                      <code className="nodes-ip" title="IPv6 address (DNS AAAA records)">
                        {node.ipv6_address}
                      </code>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`nodes-type nodes-type-${nodeType.type}`}>
                    {nodeType.label}
                    {nodeType.capabilities.supportsDns && node.dns_enabled && (
                      <span className="nodes-dns-badge" title="DNS management enabled">
                        DNS
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  <StatusIndicator status={node.status} map={nodeStatusMap} baseClassName="nodes-status" />
                </TableCell>
                <TableCell>
                  <span className={`nodes-mode nodes-mode-${node.mode}`}>{node.mode}</span>
                </TableCell>
                <TableCell>
                  <span className="nodes-heartbeat">{formatRelativeTime(node.last_heartbeat)}</span>
                </TableCell>
                <TableCell align="right">
                  <div className="nodes-actions">
                    {actions.pleskDomains && (
                      <IconButton
                        className="nodes-action-btn"
                        icon={<GlobeIcon size={16} />}
                        label={`View domains on ${node.name}`}
                        title="View Plesk domains"
                        onClick={() => onAction('pleskDomains', node)}
                      />
                    )}
                    {actions.health && (
                      <IconButton
                        className="nodes-action-btn"
                        icon={<RefreshIcon />}
                        label={`Check health of ${node.name}`}
                        title="Check health"
                        isBusy={isBusy('health', node.id)}
                        onClick={() => onAction('health', node)}
                      />
                    )}
                    {actions.edit && (
                      <IconButton
                        className="nodes-action-btn"
                        icon={<SettingsIcon />}
                        label={`Edit ${node.name}`}
                        title="Edit node"
                        onClick={() => onAction('edit', node)}
                      />
                    )}
                    {actions.delete && (
                      <IconButton
                        className="nodes-action-btn nodes-action-btn-danger"
                        icon={<TrashIcon />}
                        label={`Delete ${node.name}`}
                        title="Delete node"
                        onClick={() => onAction('delete', node)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
